// START IMPORTS
import {
  PaymentsSelectionModal,
  ViewOrderHistory,
  RadioPaymentsModal,
  PayPalRedirectionModal,
} from '@ltvco/refresh-lib/payments';
import { Grid, Box } from '@ltvco/refresh-lib/theme';
import {
  AccountInformation,
  GenericCtaCard,
  LimitedPlanProgress,
  MyAccountHeader,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { AccountEditModals } from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Internal - utils
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

// Internal - constants
import {
  AccountInformationOptions,
  GenericCtaCardProps,
  editOptionClicked,
} from './BillingConstants';
// END IMPORTS

export function Billing() {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();
  const {
    session: { account },
  } = useSession();
  const {
    isLimitedUser,
    isUnlimitedUser,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
    isLoading,
  } = useLimitedPlanInfo();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [openPaymentsSelectionModal, setOpenPaymentsSelectionModal] =
    useState<boolean>(false);
  const [openPaymentsUpdateModal, setOpenPaymentsUpdateModal] =
    useState<boolean>(false);
  const [openPaypalRedirection, setOpenPaypalRedirection] =
    useState<boolean>(false);
  const [paypalState, setPaypalState] = useState<'success' | 'canceled'>(
    'success'
  );
  const [editing, setEditing] = useState<string | null>('');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { ctaCardProps, length } = GenericCtaCardProps(navigate);
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    document.title = 'My Account - PeopleSmart';
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bvppcanc')) {
      setPaypalState('canceled');
      setOpenPaypalRedirection(true);
      return;
    }
    if (urlParams.get('bvpp') && urlParams.get('token')) {
      setPaypalState('success');
      setOpenPaypalRedirection(true);
    }
  }, []);
  // END EFFECTS

  // START EVENT_HANDLERS
  const togglePaymentsSelectionModal = () =>
    setOpenPaymentsSelectionModal(!openPaymentsSelectionModal);

  const togglePaymentsUpdateModal = () => {
    setOpenPaymentsUpdateModal(!openPaymentsUpdateModal);
    if (!openPaymentsSelectionModal) {
      togglePaymentsSelectionModal();
    }
  };

  const togglePaypalRedirectionModal = () =>
    setOpenPaypalRedirection(!openPaypalRedirection);

  const finishEditing = () => {
    setEditing('');
  };

  function handleAddPaymentMethod() {
    setOpenPaymentsSelectionModal(false);
    setOpenPaymentsUpdateModal(true);
  }
  // END EVENT_HANDLERS

  // START DATA_PREPARATION
  const has_sso_connections = account?.account?.user_info?.has_sso_connections;

  const updatedItems = has_sso_connections
    ? AccountInformationOptions.filter(
        (item) => item.constantValue !== 'CHANGE_PASSWORD'
      )
    : AccountInformationOptions;

  if (has_sso_connections) {
    updatedItems.push({
      name: 'Send Password Reset Email',
      display: 'Send Password Reset Email',
      value: 'Send Password Reset Email',
      constantValue: 'PASSWORD_RESET_EMAIL',
    });
  }
  // END DATA_PREPARATION

  // START RENDER
  return (
    <>
      <MyAccountHeader isUnlimited={isUnlimitedUser} />
      {isLimitedUser && !isLoading && (
        <LimitedPlanProgress
          progressValue={reportsRunPercentage}
          reportsLimit={reportLimit}
          reportsRun={reportsRun}
          link="/upgrade/plan"
        />
      )}

      <Grid container spacing={2} justifyContent="center">
        {[...Array(length)].map((_x, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={index}>
            <GenericCtaCard {...ctaCardProps[index]}></GenericCtaCard>
          </Grid>
        ))}
      </Grid>

      <AccountEditModals editing={editing} finishEditing={finishEditing} />
      <Box
        mt={2.5}
        nonce={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      >
        <AccountInformation
          editOptions={updatedItems}
          editOptionClick={(value: string | null) => {
            editOptionClicked(
              value,
              setOpenPaymentsSelectionModal,
              setEditing,
              updatedItems
            );
          }}
        />
      </Box>
      <ViewOrderHistory />
      <PaymentsSelectionModal
        open={openPaymentsSelectionModal}
        onClose={togglePaymentsSelectionModal}
        onAddPaymentMethod={handleAddPaymentMethod}
      />
      <RadioPaymentsModal
        isOpen={openPaymentsUpdateModal}
        onCloseHandle={togglePaymentsUpdateModal}
      />
      <PayPalRedirectionModal
        paypalState={paypalState}
        isOpen={openPaypalRedirection}
        onCloseHandle={togglePaypalRedirectionModal}
      />
    </>
  );
  // END RENDER
}
